<template>
    <div></div>
</template>

<script>
import axios from "axios";
//import html2canvas from 'html2canvas';
import { ENABLE_ASSESSMENT } from "@/config/featureFlags";
import { generateLogbookPdf } from "@/utils/pdf";

export default {
    props: {
        filteredTasks: {
            type: Array, // Assuming filteredTasks is an array
            required: true // If it's mandatory for this component to have filteredTasks
        },
        logbookName: {
            type: String,
            required: true
        },
        logbookId: {
            type: [String, Number],
            required: true
        },
        selectedTrainee: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            chapters: [],
            instructors: [],
            traineeName: '',
            traineeCategory: '',
            traineeDOB: '',
            traineeLicenseNo: '',
            CMCS_fault: '',
            extra_info: '',
            flight_deck: '',
            logbook_entry: '',
            observation: '',
            questions: '',
            formattedQuestions: '', // Declare formattedQuestions
            formattedQuestionsText: '',
            filteredTask: { questions: '' },
            continuousEvaluation: null,
            remedialEvaluation: null,
            instructorSignatures: {},
            traineeSignatures: {},
            signatures: [],
            taskAssessments: [],
            assessments: [],
            assessment_tasks: [],
            project: [],
            tasks: [],
            mergedTasks: [],
            product: {},
        };
    },
    computed: {
        filteredChapters() {
            return this.chapters.filter(chapter => chapter.tasks.all.length > 0);
        },
        totalTasks() {
            return this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.all.length, 0);
        },
        totalMandatory() {
            return this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.manob.length, 0);
        },
        totalCompleted() {
            return this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.completed.length, 0);
        },
        totalMainCompleted() {
            return this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.main_completed.length, 0);
        },
        totalEnabled() {
            return this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.enabled.length, 0);
        },
        totalOutstanding() {
            return this.filteredChapters.reduce((acc, chapter) => acc + (chapter.tasks.enabled.length - chapter.tasks.completed.length), 0);
        },
        totalCompletePercentage() {
            const totalCompleted = this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.completed.length, 0);
            const total = this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.all.length, 0);
            return ((totalCompleted / total) * 100).toFixed(1) + '%';
        },
        totalManobPercentage() {
            const totalMainCompleted = this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.main_completed.length, 0);
            const totalMandatory = this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.manob.length, 0);
            return ((totalMainCompleted / totalMandatory) * 100).toFixed(1) + '%';
        },
        totalPercentage() {
            const totalCompleted = this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.completed.length, 0);
            const total = this.filteredChapters.reduce((acc, chapter) => acc + chapter.tasks.all.length, 0);
            return ((totalCompleted / total) * 100).toFixed(1) + '%';
        },
        unsignedCompletedTasks() {
            // Filter tasks that are marked as completed but have no trainee signature
            return this.filteredTasks.filter(
                task => task.completed && !task.t_signature_id
            );
        },
        unsignedCompletedCount() {
            return this.unsignedCompletedTasks.length;
        },
        unsignedInstTasks() {
            // Filter tasks that are marked as completed but have no trainee signature
            return this.filteredTasks.filter(
                task => task.ins_so && !task.ins_signature_id
            );
        },
        unsignedInstCount() {
            return this.unsignedInstTasks.length;
        },
    },
    methods: {
        checkBeforeContinue() {
            if (this.unsignedCompletedCount > 0) {
                const proceed = confirm(
                    `There are ${this.unsignedCompletedCount} tasks signed off with no Trainee signature. Do you wish to continue?`
                );
                if (!proceed) {
                    return; // user cancelled → stop here
                }
            }
            if (this.unsignedInstCount > 0) {
                const proceed = confirm(
                    `There are ${this.unsignedInstCount} tasks signed off with no Instructor signature. Do you wish to continue?`
                );
                if (!proceed) {
                    return; // user cancelled → stop here
                }
            }
            this.getInstructorData();
        },
        async fetchChapters() {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            try {
                const response = await axios.get(`/api/chapters`, { headers });
                const chaptersData = response.data;

                // Group tasks by chapt_no
                const groupedTasks = {};
                this.mergedTasks.forEach(task => {
                    if (!groupedTasks[task.chapt_no]) {
                        groupedTasks[task.chapt_no] = {
                            all: [],
                            manob: [],
                            completed: [],
                            enabled: [],
                            main_completed: [],
                        };
                    }
                    groupedTasks[task.chapt_no].all.push(task);
                    if (task.manob === 'MT' && task.is_exam === 0) {
                        groupedTasks[task.chapt_no].manob.push(task);
                        if (task.completed) {
                            groupedTasks[task.chapt_no].main_completed.push(task);
                        }
                    }
                    if (task.enabled) groupedTasks[task.chapt_no].enabled.push(task);
                    if (task.completed) groupedTasks[task.chapt_no].completed.push(task);
                });

                // Create a map for easy lookup of chapter names
                const chapterMap = {};
                chaptersData.forEach(chapter => {
                    chapterMap[chapter.chapt_no] = chapter.name;
                });

                // Combine both: from groupedTasks (all chapt_nos used) and chapterMap
                const allChaptNos = Object.keys(groupedTasks);
                this.chapters = allChaptNos.map(chapt_no => {
                    return {
                        chapt_no,
                        name: chapterMap[chapt_no] || '', // Use empty name if not found
                        tasks: groupedTasks[chapt_no],
                    };
                });
                this.chapters.sort((a, b) => {
                    const chapRegex = /^(\d+)([A-Za-z]*)$/;
                    const [, numA, suffixA] = (a.chapt_no || '').match(chapRegex) || [];
                    const [, numB, suffixB] = (b.chapt_no || '').match(chapRegex) || [];

                    const chapterComparison = Number(numA) - Number(numB);
                    if (chapterComparison !== 0) return chapterComparison;

                    return (suffixA || '').localeCompare(suffixB || '');
                });

            } catch (error) {
                console.error('Error fetching task data:', error);
            }
        },
        getInstructorData() {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            console.log(this.logbookId);

            axios.get(`/api/logbooks/${this.logbookId}/users`, { headers })
                .then(response => {
                    const instructorData = response.data;
                    console.log(instructorData);

                    this.instructors = instructorData;
                    console.log('instructors: ', this.instructors); // Log the trainee data to see if it's fetched correctly

                    this.getProductData();
                })
                .catch(error => {
                    console.error('Error fetching trainee data:', error);
                    // Handle error accordingly
                });
        },
        getProductData() {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const productId = parseInt(localStorage.getItem('productId'));

            axios.get(`/api/products/${productId}`, { headers })
                .then(response => {
                this.product = response.data;
                console.log('product');
                console.log(this.product);
                this.getProjectData();
                })
                .catch(error => {
                console.error('There was an error fetching the task assessment:', error);
                });
        },
        getProjectData() {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            console.log(this.logbookId);
            const projectId = parseInt(localStorage.getItem('project_id'));

            axios.get(`/api/projects/${projectId}`, { headers })
                .then(response => {
                    const projectData = response.data;
                    console.log(projectData);

                    this.project = response.data.project;
                    console.log('project: ', this.project); // Log the trainee data to see if it's fetched correctly

                    if (ENABLE_ASSESSMENT) {
                        this.fetchAllAssessmentData();
                    } else {
                        this.getEvaluationData();
                    }
                })
                .catch(error => {
                    console.error('Error fetching project data:', error);
                    // Handle error accordingly
                });
        },
        fetchAllAssessmentData() {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const projectId = parseInt(localStorage.getItem('project_id'));
            const logbookId = parseInt(localStorage.getItem('logbook_id'));

            const taskRequest = axios.get(`/api/assessment_tasks/projects/${projectId}`, { headers });
            const entryRequest = axios.get(`/api/assessments/logbooks/${logbookId}`, { headers });

            Promise.all([taskRequest, entryRequest])
                .then(([taskResponse, entryResponse]) => {
                    this.assessment_tasks = taskResponse.data;
                    this.assessments = entryResponse.data;
                    this.mergeAssessmentData();
                })
                .catch(error => {
                    console.error('Error fetching data:', error);
                });
        },
        mergeAssessmentData() {
            this.taskAssessments = this.assessment_tasks.map(task => {
                const matchingAssessment = this.assessments.filter(
                    assessment => assessment.assessment_task_id === task.assessment_task_id
                );
                return {
                    ...task,
                    assessments: matchingAssessment,
                };
            });

            console.log('merged assessments', this.taskAssessments);
            this.getEvaluationData();
        },
        getEvaluationData() {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        axios.get(`/api/evaluations/logbooks/${this.logbookId}`, { headers })
            .then(response => {
            const evaluations = Array.isArray(response.data)
                ? response.data
                : response.data
                ? [response.data]
                : [];

            console.log('logbook evaluations', evaluations);

            this.continuousEvaluation = evaluations.find(
                e => e.evaluation_type === 'continuous' && e.is_submitted === 2
            ) || null;

            this.remedialEvaluation = evaluations.find(
                e => e.evaluation_type === 'remedial' && e.is_submitted === 2
            ) || null;

            this.finalizePdf();
            })
            .catch(error => {
                console.error('Error fetching Evaluation data:', error);
                this.continuousEvaluation = null;
                this.remedialEvaluation = null;
                this.finalizePdf();
            });
        },
        setBlankEvaluation() {
            this.hasEvaluation = false;
            this.criteria = {};            // no criteria answers
            this.result = '';
            this.trainee_signature = '';
            this.assessor_signature = '';
            this.evaluation_date = '';
        },
        buildPdfContext() {
            return {
                logbookName: this.logbookName,
                instructorName: localStorage.getItem('username'),
                selectedTrainee: this.selectedTrainee,
                instructors: this.instructors,
                product: this.product,
                project: this.project,
                filteredTasks: this.filteredTasks,
                filteredChapters: this.filteredChapters,
                chapters: this.chapters,
                totals: {
                    totalTasks: this.totalTasks,
                    totalMandatory: this.totalMandatory,
                    totalCompleted: this.totalCompleted,
                    totalMainCompleted: this.totalMainCompleted,
                    totalEnabled: this.totalEnabled,
                    totalOutstanding: this.totalOutstanding,
                    totalCompletePercentage: this.totalCompletePercentage,
                    totalManobPercentage: this.totalManobPercentage,
                },
                taskAssessments: this.taskAssessments,
                continuousEvaluation: this.continuousEvaluation,
                remedialEvaluation: this.remedialEvaluation,
                signatures: this.signatures,
            };
        },
        async finalizePdf() {
            await this.fetchSignaturesForTasks();
            generateLogbookPdf(this.buildPdfContext());
        },
        async fetchSignaturesForTasks() {

            try {
                // Fetch all signatures in a single API call
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                const response = await axios.get('/api/books', { headers });

                this.signatures = response.data;

                console.log(this.signatures);

            } catch (error) {
                console.error('Error fetching signatures in bulk:', error);
            }
        },
    },

    async mounted() {
        console.log('filtered');
        console.log(this.filteredTasks);
        this.mergedTasks = this.filteredTasks;

        this.fetchChapters();
        this.checkBeforeContinue();

        console.log('trainee', this.selectedTrainee);
        console.log('chapters', this.chapters);
    }
}
</script>



<style scoped>
h1.page-title {
    text-align: left;
    padding-top: 100px;
    font-size: 22px;
    margin-left: 3%;
    padding-bottom: 10px;
}
</style>